import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FinanceApi } from "api/finance/service";
import { set } from "date-fns";
import {
  MdAccessTime,
  MdGroup,
  MdLocalShipping,
  MdOutlineAssignmentReturned,
  MdOutlineVerticalAlignBottom,
  MdPayments,
  MdPerson,
  MdReceipt,
} from "react-icons/md";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import FinanceEmptyState from "assets/empty state/WalletEmpty.svg";
import RequireAccess from "components/RequireAccess";

import { X, Download, Loader } from "lucide-react";
import ReceiptDownloadButton from "./ReceiptDownloadButton";
import { BASE_URL } from "../../../utils/constant";
import { convertToStringIndex } from "../Contractors/ContractorDetails";
import { formatCurrency } from "utils/utilityFunctions";


const formatTransactionDateTime = (tran) => {
  if (!tran) return "";
  const dateStr = tran.date || tran.transaction_date || tran.created_on;
  if (!dateStr) return "";

  try {
    const datePart = new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
    const timePart = new Date(tran.created_on).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart}, ${timePart}`;
  } catch (e) {
    console.error("Error formatting date:", e);
    return "";
  }
};

// --- Modal Components copied from Finance.js ---
const MaterialPurchaseModal = ({ transaction, onClose }) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  if (!transaction) return null;

  const handleImageClick = () => setIsImageZoomed(true);
  const handleZoomClose = () => setIsImageZoomed(false);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-semibold">Material Purchase Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div
            className="px-6 py-2 overflow-y-auto"
            style={{ height: "calc(70vh - 100px)" }}
          >
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Purchase Order</span>
              <div className="text-right">
                <div>{transaction.po_number}</div>
              </div>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Transaction Type</span>
              <div className="text-right">
                <div>Part Payment</div>
              </div>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Vendor/Shop Name</span>
              <div className="text-right">
                <div>{transaction.vendor_name}</div>
                <div className="text-sm text-gray-500">
                  {transaction.vendor_contact}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Date</span>
              <div className="text-right">
                <div>
                  {new Date(transaction.transaction_date).toLocaleDateString(
                    "en-IN",
                    {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTransactionDateTime(transaction)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-gray-600 block">
                Materials ({transaction.all_materials?.length || 0})
              </span>
              <div className="space-y-3">
                {transaction.all_materials?.map((material, index) => (
                  <div
                    key={material.id || index}
                    className="bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">
                          {material.material_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {material.material_subcategory}
                        </div>
                      </div>
                      <div className="text-right">
                        <div>₹{material.total_price?.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">
                          {material.quantity} {material.unit} × ₹
                          {material.unit_price}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Method</span>
              <span>{transaction.method}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Status</span>
              <span className="capitalize">{transaction.status}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Total Amount</span>
              <span className="text-xl font-semibold">
                ₹{transaction.total_price?.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Paid Amount</span>
              <span className="text-red-600">
                ₹{transaction.paid_amount?.toLocaleString()}
              </span>
            </div>

            {/* <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Remaining Amount</span>
              <span className="text-red-600">
                ₹{transaction.remaining_payment?.toLocaleString()}
              </span>
            </div> */}

            {transaction.comments && (
              <div className="space-y-2">
                <span className="text-gray-600 block">Remarks</span>
                <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                  <div>{transaction.comments}</div>
                </div>
              </div>
            )}

            {transaction.receipt_image && (
              <div className="space-y-2">
                <span className="text-gray-600 block">Receipt</span>
                <div
                  className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={handleImageClick}
                  title="Click to zoom"
                >
                  <img
                    src={transaction.receipt_image}
                    alt="Receipt"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end p-6 ">
              <ReceiptDownloadButton
                transaction={transaction}
                type="material"
                buttonText={
                  <div className="flex items-center gap-2">
                    <Download size={16} />
                    <span>View Receipt</span>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {isImageZoomed && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]">
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              onClick={handleZoomClose}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              title="Close zoom"
            >
              <X size={32} />
            </button>
            <img
              src={transaction.receipt_image}
              alt="Receipt - Zoomed"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

const WorkerTransactionModal = ({ transaction, onClose }) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  if (!transaction) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-semibold">Workers Payment Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div
            className="p-6 overflow-y-scroll"
            style={{ height: "calc(70vh - 110px)" }}
          >
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Receiver</span>
              <div className="text-right">
                <div>{transaction.worker_name}</div>
                <div className="text-sm text-gray-500">
                  {transaction.worker_workname}
                </div>
              </div>
            </div>

            {/* <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Receiver Phone Number</span>
              <span>{transaction?.worker_phone || "+91-9876543210"}</span>
            </div> */}

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Created By</span>
              <div className="text-right">
                <div>{transaction.created_by}</div>
                {transaction.last_updated_by && (
                  <div className="text-sm text-gray-500">
                    Last updated by: {transaction.last_updated_by}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Date</span>
              <div className="text-right">
                <div>
                  {new Date(
                    transaction.date || transaction.transaction_date
                  ).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTransactionDateTime(transaction)}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Amount</span>
              <span className="text-xl font-semibold">
                ₹{transaction.amount?.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Method</span>
              <span>{transaction.method}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Type</span>
              <span>{transaction.payment_type}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Total Paid Amount</span>
              <span className="text-green-600">
                ₹{(transaction.amount || 0).toLocaleString()}
              </span>
            </div>

            <div className="space-y-3 py-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Comments/Remarks</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <div>{transaction?.comments}</div>
              </div>
            </div>

            {transaction.receipt_url && (
              <div className="space-y-2">
                <span className="text-gray-600 block">Payment Slips</span>
                <div className="flex gap-4">
                  <div
                    className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setIsImageZoomed(true)}
                  >
                    <img
                      src={transaction.receipt_url}
                      alt="Payment slip - click to zoom"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end p-6 border-t">
              <ReceiptDownloadButton
                transaction={transaction}
                type="worker"
                buttonText={
                  <div className="flex items-center gap-2">
                    <Download size={16} />
                    <span>View Receipt</span>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {isImageZoomed && transaction.receipt_image && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setIsImageZoomed(false)}
              className="absolute top-2 right-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors z-10"
            >
              <X size={24} />
            </button>
            <img
              src={transaction.receipt_image}
              alt="Payment slip - zoomed view"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

const RefundTransactionModal = ({ transaction, onClose }) => {
  if (!transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl ">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Refund Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div
          className="p-6 overflow-y-scroll"
          style={{ height: "calc(70vh - 110px)" }}
        >
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Transaction Type</span>
            <span>{transaction.description}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Purpose of Refund</span>
            <span>{transaction.original_transaction_type}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Original Transaction Id</span>
            <span className="font-medium">{`TRN-${convertToStringIndex(
              transaction.original_transaction_id
            )}`}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Date</span>
            <div className="text-right">
              <div>
                {new Date(
                  transaction.date || transaction.transaction_date
                ).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {formatTransactionDateTime(transaction)}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Created By</span>
            <span className="">{transaction.created_by || "N/A"}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Refund Amount</span>
            <span className="text-xl font-semibold">
              ₹{transaction.amount?.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Refund Method</span>
            <span>{transaction.method}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Refund Status</span>
            <span>{transaction.status}</span>
          </div>

          <div className="space-y-2 py-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Comments/Remarks</span>
            </div>
            <div className="space-y-3">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div>{transaction.comments}</div>
              </div>
            </div>
          </div>

          {transaction.receipt_url && (
            <div className="space-y-2">
              <span className="text-gray-600 block">Payment Slips</span>
              <div className="flex gap-4">
                <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                  <img
                    src={transaction.receipt_url}
                    alt="Payment slip"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end p-6 border-t">
            <ReceiptDownloadButton
              transaction={transaction}
              type="refund"
              buttonText={
                <div className="flex items-center gap-2">
                  <Download size={16} />
                  <span>View Receipt</span>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const OtherTransactionModal = ({ transaction, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [editedTransaction, setEditedTransaction] = useState({
    transaction_type: transaction?.description || "",
    transaction_method: transaction?.method || "",
    comments: transaction?.comments || "",
    amount: transaction?.amount || 0,
    transaction_date: transaction?.transaction_date
      ? new Date(transaction.transaction_date).toISOString().split("T")[0]
      : "",
  });

  if (!transaction) return null;
  // console.log("OtherTransactionModal transaction:", transaction);
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedTransaction((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) : value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const requestBody = {
        ...editedTransaction,
        sites: localStorage.getItem("site"),
      };
      const response = await fetch(
        `${BASE_URL}/finance/update-general-transaction/${transaction.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        setIsEditing(false);
        if (onUpdate) onUpdate();
        onClose();
      } else {
        console.error("Failed to update transaction");
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
    }
  };

  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/finance/delete-general-transaction/${transaction.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sites: localStorage.getItem("site"),
          }),
        }
      );

      if (response.ok) {
        setShowDeleteConfirmation(false);
        onClose();
        if (onUpdate) onUpdate();
      } else {
        console.error("Failed to delete transaction");
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg h-[74vh] w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {isEditing ? "Edit Transaction" : "Other Transaction Details"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div
          className="p-6 space-y-4 overflow-y-auto"
          style={{ height: "calc(70vh - 130px)" }}
        >
          {isEditing ? (
            <>
              <div className="space-y-2">
                <label className="block text-gray-600">Transaction Type</label>
                <input
                  type="text"
                  name="transaction_type"
                  value={editedTransaction.transaction_type}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-gray-600">Payment Method</label>
                <select
                  name="transaction_method"
                  value={editedTransaction.transaction_method}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a method</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI/Bank Transfer">UPI/Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-gray-600">Amount</label>
                <input
                  type="number"
                  name="amount"
                  value={editedTransaction.amount}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-gray-600">Transaction Date</label>
                <input
                  type="date"
                  name="transaction_date"
                  value={editedTransaction.transaction_date}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-gray-600">Remarks</label>
                <textarea
                  name="comments"
                  value={editedTransaction.comments}
                  onChange={handleEditChange}
                  rows={4}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Transaction Type</span>
                <div className="text-right">
                  <div>{transaction.description}</div>
                </div>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Created By</span>
                <div className="text-right">
                  <div>{transaction.created_by}</div>
                  <div className="text-sm text-gray-500">
                    Last updated by: {transaction.last_updated_by}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Transaction Date</span>
                <div className="text-right">
                  <div>
                    {new Date(transaction.transaction_date).toLocaleDateString(
                      "en-IN",
                      { day: "numeric", month: "long", year: "numeric" }
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatTransactionDateTime(transaction)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Amount</span>
                <span className="text-xl font-semibold">
                  ₹{transaction.amount?.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Payment Method</span>
                <span>{transaction.method}</span>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Created On</span>
                <div className="text-right">
                  <div>
                    {new Date(transaction.created_on).toLocaleString("en-IN")}
                  </div>
                  <div className="text-sm text-gray-500">
                    Last updated:{" "}
                    {new Date(transaction.last_updated_on).toLocaleString(
                      "en-IN"
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Remarks</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                  <div>{transaction.comments}</div>
                </div>
              </div>

              {transaction.ireceipt_image && (
                <div className="space-y-2">
                  <span className="text-gray-600 block">Receipt Image</span>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div
                      className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setShowImageModal(true)}
                    >
                      <img
                        src={transaction.ireceipt_image}
                        alt="Receipt"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Click to view full size
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex justify-between p-6 border-t">
            {isEditing ? (
              <>
                <button
                  onClick={handleSaveChanges}
                  className="px-6 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 flex items-center"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-6 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 flex items-center"
                >
                  Edit Details
                </button>
                <button
                  onClick={() => setShowDeleteConfirmation(true)}
                  className="px-6 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 flex items-center"
                >
                  Delete Transaction Record
                </button>
                <ReceiptDownloadButton
                  transaction={transaction}
                  type="other"
                  buttonText={
                    <div className="flex items-center gap-2">
                      <Download size={16} />
                      <span>View Receipt</span>
                    </div>
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>

      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Confirm Deletion</h3>
            <p className="mb-6">
              Are you sure you want to delete this transaction? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showImageModal && transaction.ireceipt_image && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
            <img
              src={transaction.ireceipt_image}
              alt="Receipt - Full Size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const VendorTransactionModal = ({ transaction, onClose, onUpdate }) => {
  const [showImageModal, setShowImageModal] = useState(false);

  if (!transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg h-[74vh] w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Vendor Transaction Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div
          className="p-6 space-y-4 overflow-y-auto"
          style={{ height: "calc(70vh - 130px)" }}
        >
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Transaction Type</span>
            <div className="text-right">
              <div>Vendor Advance Payment</div>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Vendor</span>
            <div className="text-right">
              <div>{transaction.vendor_name}</div>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Created By</span>
            <div className="text-right">
              <div>{transaction.created_by}</div>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Transaction Date</span>
            <div className="text-right">
              <div>
                {new Date(transaction.transaction_date).toLocaleDateString(
                  "en-IN",
                  { day: "numeric", month: "long", year: "numeric" }
                )}
              </div>
              <div className="text-sm text-gray-500">
                {formatTransactionDateTime(transaction)}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Amount</span>
            <span className="text-xl font-semibold">
              ₹{transaction.amount?.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Payment Method</span>
            <span>{transaction.method}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Created On</span>
            <div className="text-right">
              <div>
                {new Date(transaction.created_on).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Remarks</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg space-y-1">
              <div>{transaction.comments}</div>
            </div>
          </div>

          {transaction.receipt_url && (
            <div className="space-y-2">
              <span className="text-gray-600 block">Receipt Image</span>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div
                  className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowImageModal(true)}
                >
                  <img
                    src={transaction.receipt_url}
                    alt="Receipt"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Click to view full size
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between p-6 border-t">
            {/* <button
              onClick={() => setIsEditing(true)}
              className="px-6 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 flex items-center"
            >
              Edit Details
            </button>
            <button
              onClick={() => setShowDeleteConfirmation(true)}
              className="px-6 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 flex items-center"
            >
              Delete Transaction Record
            </button> */}
            <ReceiptDownloadButton
              transaction={transaction}
              type="vendor"
              buttonText={
                <div className="flex items-center gap-2">
                  <Download size={16} />
                  <span>View Receipt</span>
                </div>
              }
            />
          </div>
        </div>
      </div>

      {showImageModal && transaction.receipt_url && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
            <img
              src={transaction.ireceipt_image}
              alt="Receipt - Full Size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ContractorTransactionModal = ({ transaction, onClose, onUpdate }) => {
  const [showImageModal, setShowImageModal] = useState(false);

  if (!transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg h-[74vh] w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Contractor Payment Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div
          className="p-5 overflow-y-auto"
          style={{ height: "calc(74vh - 110px)" }}
        >
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Transaction Type</span>
            <div className="text-right">
              <div>Contractor Payment</div>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Contractor</span>
            <div className="text-right">
              <div>{transaction.contractor_name}</div>
            </div>
          </div>
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Work Order</span>
            <div className="text-right">
              <div>{transaction.description}</div>
            </div>
          </div>
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Created By</span>
            <div className="text-right">
              <div>{transaction.created_by}</div>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Transaction Date</span>
            <div className="text-right">
              <div>
                {new Date(transaction.transaction_date).toLocaleDateString(
                  "en-IN",
                  { day: "numeric", month: "long", year: "numeric" }
                )}
              </div>
              <div className="text-sm text-gray-500">
                {formatTransactionDateTime(transaction)}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Amount</span>
            <span className="text-xl font-semibold">
              ₹{transaction.amount?.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Payment Method</span>
            <span>{transaction.method}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Created On</span>
            <div className="text-right">
              <div>
                {new Date(transaction.created_on).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Remarks</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg space-y-1">
              <div>{transaction.comments}</div>
            </div>
          </div>

          {transaction.receipt_url && (
            <div className="space-y-2">
              <span className="text-gray-600 block">Receipt Image</span>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div
                  className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowImageModal(true)}
                >
                  <img
                    src={transaction.receipt_url}
                    alt="Receipt"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Click to view full size
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between p-6 border-t">
            <ReceiptDownloadButton
              transaction={transaction}
              type="contractor"
              buttonText={
                <div className="flex items-center gap-2">
                  <Download size={16} />
                  <span>View Receipt</span>
                </div>
              }
            />
          </div>
        </div>
      </div>

      {showImageModal && transaction.receipt_url && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
            <img
              src={transaction.ireceipt_image}
              alt="Receipt - Full Size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const PendingTransactionModal = ({ transaction, onClose }) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  if (!transaction) return null;

  const handleImageClick = () => setIsImageZoomed(true);
  const handleZoomClose = () => setIsImageZoomed(false);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-semibold">Pending Transaction</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div
            className="px-6 py-2 overflow-y-auto"
            style={{ height: "calc(70vh - 100px)" }}
          >
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Purchase Order</span>
              <div className="text-right">
                <div>{transaction.po_number}</div>
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Vendor/Shop Name</span>
              <div className="text-right">
                <div>{transaction.vendor_name}</div>
                <div className="text-sm text-gray-500">
                  {transaction.vendor_contact}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Created On</span>
              <div className="text-right">
                <div>
                  {new Date(transaction.created_on).toLocaleDateString(
                    "en-IN",
                    {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTransactionDateTime(transaction)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-gray-600 block">
                Materials ({transaction.all_materials?.length || 0})
              </span>
              <div className="space-y-3">
                {transaction.all_materials?.map((material, index) => (
                  <div
                    key={material.id || index}
                    className="bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">
                          {material.material_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {material.material_subcategory}
                        </div>
                      </div>
                      <div className="text-right">
                        <div>₹{material.total_price?.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">
                          {material.quantity} {material.unit} × ₹
                          {material.unit_price}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Method</span>
              <span>{transaction.method}</span>
            </div> */}

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Status</span>
              <span className="capitalize">{transaction.status}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Total Amount</span>
              <span className="text-xl font-semibold">
                ₹{transaction.total_amount?.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Paid Amount</span>
              <span className="text-red-600">
                ₹{transaction.paid_amount?.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Remaining Amount</span>
              <span className="text-red-600">
                ₹{transaction.remaining_payment?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {isImageZoomed && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]">
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              onClick={handleZoomClose}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              title="Close zoom"
            >
              <X size={32} />
            </button>
            <img
              src={transaction.receipt_image}
              alt="Receipt - Zoomed"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

// --- End of copied components ---

function StatCard({ title, value }) {
  return (
    <div className="flex-1 bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-sm text-gray-500 mb-2">{title}</p>
      <p className="text-xl font-semibold">₹ {value}</p>
    </div>
  );
}

function MetricCard({
  title,
  value,
  percent = 12,
  color = "#10B981",
  trend = "up",
  data: inputData,
  showPeriodSelector,
  onPeriodChange,
  currentPeriod,
}) {
  const data =
    inputData && inputData.length > 0
      ? inputData
      : [30, 31, 31, 32, 33, 34, 34, 35, 36, 37, 38];
  const width = 100;
  const height = 40;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = Math.max(1, max - min);
  const stepX = width / (data.length - 1);
  const points = data
    ?.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(" ");
  const firstY = height - ((data[0] - min) / range) * height;
  const lastX = (data.length - 1) * stepX;
  const linePath = `M0,${firstY} L ${points}`;
  const areaPath = `${linePath} L ${lastX},${height} L 0,${height} Z`;
  const gradientId = `kpiGradient-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const accentColor = color || (trend === "down" ? "#F97316" : "#10B981");
  const percentTextClass =
    trend === "down" ? "text-orange-500" : "text-emerald-600";
  const periodLabels = {
    "7days": "Last 7 days",
    "30days": "Last 30 days",
    "6months": "Last 6 months",
    "1year": "Last year",
    all: "All time",
  };
  const isPositive = percent >= 0;
  const percentColor = isPositive ? "text-emerald-600" : "text-red-600";
  const arrowPath = isPositive
    ? "M10 4l5 6h-3v6H8v-6H5l5-6z"
    : "M10 16l5-6h-3V4H8v6H5l5 6z";

  return (
    <div className="flex-1 bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start justify-between mb-2">
        <p className="text-sm text-gray-500">{title}</p>
        {showPeriodSelector ? (
          <div className="relative">
            <select
              value={currentPeriod}
              onChange={(e) => onPeriodChange(e.target.value)}
              className="text-xs text-gray-500 border border-gray-300 rounded px-2 py-1 pr-6 appearance-none bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:border-purple-500"
            >
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="6months">Last 6 months</option>
              <option value="1year">Last year</option>
              <option value="all">All time</option>
            </select>
            <svg
              width="12"
              height="12"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="text-gray-400 absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none"
            >
              <path
                d="M5.5 7.5l4.5 4.5 4.5-4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>{periodLabels[currentPeriod] || "Month to date"}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-2xl font-semibold">₹ {value}</p>
        {percent !== 0 && (
          <div className={`flex items-center gap-1 ${percentColor} text-sm`}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 20 20"
              fill="currentColor"
              className={percentColor}
            >
              <path d={arrowPath} />
            </svg>
            <span>{Math.abs(percent)}%</span>
          </div>
        )}
      </div>
      <div className="mt-3 h-16">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.35" />
              <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path d={linePath} fill="none" stroke={accentColor} strokeWidth="2" />
        </svg>
      </div>
    </div>
  );
}

function CompanyExpensesCard({ value }) {
  const data = [30, 31, 31, 32, 33, 34, 34, 35, 36, 37, 38];
  return (
    <MetricCard
      title="Company expenses"
      value={value}
      percent={12}
      data={data}
      showPeriodSelector={true}
    />
  );
}
function TotalDuesCard({ value }) {
  const data = [40, 39, 38, 39, 40, 41, 40, 41, 42, 43, 44];
  return (
    <MetricCard title="Total Dues" value={value} percent={12} data={data} />
  );
}
function TotalReceivedCard({ value }) {
  const data = [20, 21, 22, 23, 24, 26, 27, 28, 28, 29, 30];
  return (
    <MetricCard title="Total Received" value={value} percent={12} data={data} />
  );
}
function TotalRemainingCard({ value }) {
  const data = [50, 50, 49, 48, 49, 49, 50, 50, 51, 52, 52];
  return (
    <MetricCard
      title="Total Remaining"
      value={value}
      percent={12}
      data={data}
    />
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1 rounded-full text-sm border ${active
        ? "bg-purple-100 text-purple-900 border-purple-300"
        : "bg-white text-gray-700 border-gray-200"
        }`}
    >
      {children}
    </button>
  );
}

function SubTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 mr-2 flex items-center gap-2 whitespace-nowrap ${active
        ? "border-[#8067E0] text-[#8067E0]"
        : "border-transparent text-[#737373]"
        }`}
    >
      {label === "All Transactions" ? (
        <MdReceipt className="w-4 h-4" color={active ? "#8067E0" : "#737373"} />
      ) : label === "Material" ? (
        <MdLocalShipping
          className="w-5 h-5"
          color={active ? "#8067E0" : "#737373"}
        />
      ) : label === "Advance Payments" ? (
        <MdGroup className="w-5 h-5" color={active ? "#8067E0" : "#737373"} />
      ) : label === "Contractor" ? (
        <MdGroup className="w-5 h-5" color={active ? "#8067E0" : "#737373"} />
      ) : label === "Worker" ? (
        <MdPerson className="w-5 h-5" color={active ? "#8067E0" : "#737373"} />
      ) : label === "Pending transaction" ? (
        <MdAccessTime
          className="w-5 h-5"
          color={active ? "#8067E0" : "#737373"}
        />
      ) : label === "Other transaction" ? (
        <MdPayments
          className="w-5 h-5"
          color={active ? "#8067E0" : "#737373"}
        />
      ) : label === "Refund" ? (
        <MdOutlineAssignmentReturned
          className="w-5 h-5"
          color={active ? "#8067E0" : "#737373"}
        />
      ) : label === "Received transaction" ? (
        <MdOutlineVerticalAlignBottom
          className="w-5 h-5"
          color={active ? "#8067E0" : "#737373"}
        />
      ) : null}
      <span>{label}</span>
    </button>
  );
}

export default function Finance({ currentMember }) {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [scope, setScope] = useState("global");
  const [activeTab, setActiveTab] = useState("All Transactions");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // --- Renamed state to hold RAW API data ---
  const [allRowsRaw, setAllRowsRaw] = useState([]);
  const [pendingTransactionsRaw, setPendingTransactionsRaw] = useState([]);

  const [paymentSummary, setPaymentSummary] = useState({});
  const showTable = true;
  const [chartPeriod, setChartPeriod] = useState("30days");
  const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

  // --- Extracted fetchTransactions to be reusable ---
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
      if (!orgId) {
        setAllRowsRaw([]);
        setPendingTransactionsRaw([]);
        return;
      }
      const res = await FinanceApi.listTransactionsByOrganization(
        orgId,
        chartPeriod
      );

      // --- Store the RAW data, not normalized data ---
      setAllRowsRaw(res?.data || []);
      setPendingTransactionsRaw(res?.pending_transactions || []);
      setPaymentSummary(res?.payment_summary || {});
    } catch (e) {
      setError(e?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [chartPeriod]);

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled) {
      fetchTransactions();
    }
    return () => {
      isCancelled = true;
    };
  }, [fetchTransactions]);

  // --- visibleRows now normalizes data on-the-fly ---
  const visibleRows = useMemo(() => {
    let rowsToProcess = [];

    if (activeTab === "All Transactions") {
      rowsToProcess = allRowsRaw;
    } else if (activeTab === "Material") {
      rowsToProcess = allRowsRaw.filter(
        (r) => r.category === "Material Transaction"
      );
    } else if (activeTab === "Worker") {
      rowsToProcess = allRowsRaw.filter(
        (r) => r.category === "Worker Transaction"
      );
    } else if (activeTab === "Other transaction") {
      rowsToProcess = allRowsRaw.filter(
        (r) => r.category === "General Transaction"
      );
    } else if (activeTab === "Pending transaction") {
      rowsToProcess = pendingTransactionsRaw;
    } else if (activeTab === "Advance Payments") {
      rowsToProcess = allRowsRaw.filter(
        (r) => r.category === "Vendor Advance Payment"
      );
    } else if (activeTab === "Contractor") {
      rowsToProcess = allRowsRaw.filter(
        (r) => r.category === "Contractor Payment"
      );
    } else if (activeTab === "Refund") {
      rowsToProcess = allRowsRaw.filter((r) => r.category === "Refund");
    } else if (activeTab === "Received transaction") {
      rowsToProcess = allRowsRaw.filter((r) => r.category === "Received");
    } else {
      rowsToProcess = allRowsRaw;
    }

    if (activeTab === "Pending transaction") {
      return rowsToProcess.map((t) => ({
        project: t.site_name || "Organization Purchase",
        desc: t.description || "-",
        id: `${t.transaction_id}`,
        type: t.category || "-",
        medium: t.method || "-",
        total: t.total_amount || 0,
        paid_amount: t.paid_amount || 0,
        date: t.transaction_date || "-",
        remaining_payment: t.remaining_payment || 0,
        positive: false,
        category: "Pending",
        vendor_name: t.vendor_name || "-",
        originalTransaction: t,
      }));
    } else {
      return rowsToProcess.map((t) => {
        const isMaterial = t.transaction_type === "vendor_material";
        const amountValue = isMaterial ? t.paid_amount : t.amount;
        return {
          project: t.site_name || "Organization Purchase",
          desc: t.description || "-",
          id: `${t.transaction_id}`,
          type: t.category || "-",
          medium: t.method || "-",
          contractor_name: t.contractor_name || "-",
          vendor_name: t.vendor_name || "-",
          date: t.transaction_date || "-",
          amount: amountValue ?? 0,
          positive:
            t.category === "Received" || t.category === "Refund" ? true : false,
          category: t.category || "Others",
          originalTransaction: t,
        };
      });
    }
  }, [activeTab, allRowsRaw, pendingTransactionsRaw]);

  // --- Modal close handler ---
  const handleCloseModal = () => setSelectedTransaction(null);

  if (!organizationId) {
    return <OrganizationEmptyState />;
  }

  if (
    currentMember?.finance_control === false &&
    currentMember?.joined_as !== "owner" &&
    currentMember?.is_admin === false
  ) {
    return (
      <RequireAccess
        module="Finance"
        currentMember={currentMember}
        permission="finance_control"
      />
    );
  }

  return (
    <div className="w-[85%] py-2 ml-2 h-[90vh] flex flex-col overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 mt-2">
          <Pill active={scope === "global"} onClick={() => setScope("global")}>
            Global Finance
          </Pill>
          {/* <Pill
            active={scope === "project"}
            onClick={() => setScope("project")}
          >
            Project Finance
          </Pill> */}
        </div>
      </div>

      <div className="mb-4 mr-4">
        <div className="flex items-center justify-between mb-3"></div>
        <div className="flex gap-3">
          <MetricCard
            title="Company expenses"
            value={paymentSummary?.total_paid?.toLocaleString("en-IN") || 0}
            percent={paymentSummary?.chart_data?.expenses_percent || 0}
            data={paymentSummary?.chart_data?.expenses}
            showPeriodSelector={true}
            onPeriodChange={setChartPeriod}
            currentPeriod={chartPeriod}
          />
          <MetricCard
            title="Total Dues"
            value={paymentSummary?.total_pending?.toLocaleString("en-IN") || 0}
            percent={paymentSummary?.chart_data?.dues_percent || 0}
            data={paymentSummary?.chart_data?.dues}
            currentPeriod={chartPeriod}
            color="#F97316"
            trend="down"
          />
          <MetricCard
            title="Total Received"
            value={paymentSummary?.total_received?.toLocaleString("en-IN") || 0}
            percent={paymentSummary?.chart_data?.received_percent || 0}
            data={paymentSummary?.chart_data?.received}
            currentPeriod={chartPeriod}
          />
          <MetricCard
            title="Total Remaining"
            value={
              (
                paymentSummary?.total_received - paymentSummary?.total_paid
              )?.toLocaleString("en-IN") || 0
            }
            percent={paymentSummary?.chart_data?.remaining_percent || 0}
            data={paymentSummary?.chart_data?.remaining}
            currentPeriod={chartPeriod}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 mr-4">
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="text-base font-semibold text-[#343C6A]">
            Transaction Details
          </h3>
        </div>

        <div className="px-4 pt-1">
          <div className="flex items-center justify-between mb-4">
            <div
              className="flex items-center overflow-x-scroll"
              style={{ scrollbarWidth: "none" }}
            >
              {[
                { label: "All Transactions" },
                { label: "Material" },
                { label: "Contractor" },
                { label: "Worker" },
                { label: "Pending transaction" },
                { label: "Other transaction" },
                { label: "Refund" },
                { label: "Advance Payments" },
                { label: "Received transaction" },
              ].map((item) => (
                <SubTab
                  key={item.label}
                  label={item.label}
                  active={activeTab === item.label}
                  onClick={() => setActiveTab(item.label)}
                />
              ))}
            </div>
          </div>
        </div>

        {visibleRows?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-gray-500">
                  <th className="text-left font-medium pr-2 pl-5 pb-3 text-[#718EBF]">
                    Project
                  </th>
                  <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                    Description
                  </th>
                  {activeTab === "Contractor" && (
                    <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                      Contractor Name
                    </th>
                  )}
                  {activeTab === "Advance Payments" && (
                    <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                      Vendor Name
                    </th>
                  )}
                  <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                    Transaction ID
                  </th>
                  <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                    {activeTab === "Pending transaction" ||
                      activeTab === "Material"
                      ? "Vendor"
                      : "Type"}
                  </th>
                  <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                    {activeTab === "Pending transaction"
                      ? "Total Amount"
                      : "Medium"}
                  </th>
                  <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                    {activeTab === "Pending transaction"
                      ? "Amount Paid"
                      : "Date"}
                  </th>
                  <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                    {activeTab === "Pending transaction"
                      ? "Amount Due"
                      : "Amount"}
                  </th>
                  <th className="text-left font-medium px-2 pb-3 text-[#718EBF]">
                    Receipt
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {visibleRows?.map((transaction, idx) => (
                  <tr
                    key={idx}
                    className="border-t border-gray-200 hover:bg-gray-50"
                  >
                    <td className="pr-2 pl-5 py-3">{transaction.project}</td>
                    <td className="px-2 py-3">{transaction.desc}</td>

                    {activeTab === "Contractor" && (
                      <td className="px-2 py-3">
                        {transaction.contractor_name}
                      </td>
                    )}
                    {activeTab === "Advance Payments" && (
                      <td className="px-2 py-3">{transaction.vendor_name}</td>
                    )}

                    <td className="px-2 py-3">{transaction.id}</td>
                    <td className="px-2 py-3">
                      {activeTab === "Pending transaction" ||
                        activeTab === "Material"
                        ? transaction.vendor_name
                        : transaction.type}
                    </td>

                    <td className="px-2 py-3">
                      {activeTab === "Pending transaction"
                        ? `₹ ${formatCurrency(transaction.total)}`
                        : transaction.medium}
                    </td>

                    <td className="px-2 py-3">
                      {activeTab === "Pending transaction"
                        ? `₹ ${formatCurrency(transaction.paid_amount)}`
                        : transaction.date}
                    </td>

                    <td
                      className={`px-2 py-3 font-medium text-base ${transaction.positive
                        ? "text-emerald-600"
                        : "text-red-500"
                        }`}
                    >
                      ₹{" "}
                      {(() => {
                        const value =
                          activeTab === "Pending transaction"
                            ? transaction.remaining_payment
                            : transaction.amount;

                        return value % 1 === 0
                          ? value.toLocaleString("en-IN") // integer: no decimals
                          : value.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }); // decimals
                      })()}
                    </td>

                    <td className="py-2 px-3">
                      <button
                        className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                        onClick={() => {
                          setSelectedTransaction(transaction);
                          console.log(transaction);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col px-5 py-16 text-center text-gray-500 text-sm">
            {loading ? (
              "Loading transactions..."
            ) : (
              <div className="flex flex-col items-center">
                <img
                  src={FinanceEmptyState}
                  draggable={false}
                  alt="No transactions"
                  className="mb-4 h-48 w-48"
                />
                <span>No transactions found.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal rendering logic — uses the original transaction object */}
      {selectedTransaction &&
        (() => {
          const type = selectedTransaction.transaction_type;
          const cat = selectedTransaction.category;

          if (cat === "Pending Transaction" || cat === "Material Transaction") {
            return (
              <MaterialPurchaseModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
              />
            );
          }
          if (cat === "Worker Transaction") {
            return (
              <WorkerTransactionModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
              />
            );
          }
          if (cat === "Refund") {
            return (
              <RefundTransactionModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
              />
            );
          }
          if (cat === "General Transaction" || cat === "Received") {
            return (
              <OtherTransactionModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
                onUpdate={fetchTransactions}
              />
            );
          }

          if (cat === "Vendor Advance Payment") {
            return (
              <VendorTransactionModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
                onUpdate={fetchTransactions}
              />
            );
          }
          if (cat === "Contractor Payment") {
            return (
              <ContractorTransactionModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
                onUpdate={fetchTransactions}
              />
            );
          }

          if (cat === "Vendor Advance Payment") {
            return (
              <OtherTransactionModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
                onUpdate={fetchTransactions}
              />
            );
          }

          if (cat === "Pending") {
            return (
              <PendingTransactionModal
                transaction={selectedTransaction.originalTransaction}
                onClose={handleCloseModal}
              />
            );
          }

          return null;
        })()}
    </div>
  );
}